import { ScramjetClient } from "@client/index";
import { unrewriteUrl } from "@rewriters/url";
import { _URL } from "@/shared/snapshot";

/**
 * The meta to rewrite code made at run time with (new Function, eval). In browsers an
 * import() in such code resolves against the script that made it (ECMAScript: the function
 * keeps the ScriptOrModule active when it is created), not against the page. Usercentrics'
 * consent banner makes `new Function("u", "return import(u)")` in its module and imports
 * "./DefaultData-….js" through it; resolved against the page, the banner's parts were missing.
 * The creating script is the first proxied address on the call stack.
 */
export function callerMeta(client: ScramjetClient) {
	let stack = "";
	const ErrorCtor = client.global.Error as ErrorConstructor & {
		stackTraceLimit?: number;
	};
	try {
		const limit = ErrorCtor.stackTraceLimit;
		ErrorCtor.stackTraceLimit = 40;
		stack = String(new ErrorCtor().stack || "");
		ErrorCtor.stackTraceLimit = limit;
	} catch {
		return client.meta;
	}
	const prefix = client.context.prefix.href;
	for (const line of stack.split("\n")) {
		const at = line.indexOf(prefix);
		if (at < 0) continue;
		const address = /^[^\s)]+/.exec(line.slice(at));
		if (!address) continue;
		const proxied = address[0].replace(/(:\d+){1,2}$/, "");
		try {
			const real = new _URL(unrewriteUrl(proxied, client.context));
			if (real.protocol !== "http:" && real.protocol !== "https:") continue;
			// Code in the page itself (an inline script): the page's own base address applies.
			if (real.href === client.url.href) return client.meta;

			return { ...client.meta, base: real };
		} catch {
			continue;
		}
	}

	return client.meta;
}
